import { useCallback, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { FormPart } from '@modules/shared/components/FormPart';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { LoadingButton } from '@shared/inputs/Buttons';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { openPdfByUrl } from '@shared/commonFunctions';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Roles } from '@shared/constants/constants';
import { IFormAsset, VALUATION_TYPES } from '../interfaces';
import { requestMSRPData } from '../requests';
import { VehicleFeaturesModal } from './VehicleFeaturesModal';
import { VehicleOptionsModal } from './VehicleOptionsModal';

interface IValuationFormProps {
  zip?: string | null;
  state?: string | null;
  pickedDealId?: string;
  autoPullValuation: boolean;
  autoPullValuationComplete: (isSuccess: boolean) => void;
}
export const ValuationWrapper = styled.div`
  position: relative;
`;

export const VehicleOptionsButton = styled(LoadingButton)({
  position: 'absolute',
  right: 0,
});

export const ValuationForm = ({
  zip,
  state,
  pickedDealId,
  autoPullValuation,
  autoPullValuationComplete,
}: IValuationFormProps) => {
  const { values, setFieldValue, validateField } = useFormikContext<IFormAsset>();
  const { isAdmin, userHasRoles } = useUserAccount();

  const disableMSRP = !(isAdmin || userHasRoles([Roles.PULLMSRP]));
  const hasEditBookoutRole = userHasRoles([Roles.EDIT_BOOKOUT]);

  const [kbbValuationType, setKbbValuationType] = useState<string | null>(null);
  const [kbbValueTypePosition, setKbbValuitionTypePosition] = useState(0);
  const [nadaValuationType, setNadaValuationType] = useState<string | null>(null);
  const [nadaValueTypePosition, setNadaValuitionTypePosition] = useState(0);

  const [kbbValuationFieldsEdit, setKbbValuationFieldsEdit] = useState(hasEditBookoutRole);
  const [nadaValuationFieldsEdit, setNadaValuationEdit] = useState(hasEditBookoutRole);

  const [kbbValuationPulled, setKbbValuationPulled] = useState(false);
  const [nadaValuationPulled, setNadaValuationPulled] = useState(false);

  const [autoSubmitOptionsPopup, setAutoSubmitOptionsPopup] = useState(false);
  const [displayVehicleOptionsPopup, setDisplayVehicleOptionsPopup] = useState(false);

  const [isMsrpLoading, setMsrpLoadingStatus] = useState(false);

  useEffect(() => {
    const isEditable = isAdmin || hasEditBookoutRole;
    setNadaValuationEdit(isEditable);
    setKbbValuationFieldsEdit(isEditable);
  }, [userHasRoles, isAdmin]);

  const validate = useCallback(
    (valuationType: VALUATION_TYPES): boolean => {
      const { id, vin, mileage } = values;

      if (!id) {
        Notistack.generalError('Please save asset or select an existing asset');
      } else if (mileage && vin && state && zip) {
        return true;
      } else if (!(state && zip)) {
        Notistack.generalError(
          `Contact must have active address with State and Zip to pull ${valuationType.toUpperCase()} Valuation`
        );
      } else {
        validateField('vin');
        Notistack.generalError(`Vin and Odometer required to pull ${valuationType.toUpperCase()} Valuation`);
      }
      return false;
    },
    [zip, values, state]
  );

  const handleNadaClick = useCallback(
    (showPopup: boolean) => {
      if (validate(VALUATION_TYPES.Nada)) {
        if (values.nadaTrim) {
          setAutoSubmitOptionsPopup(!showPopup);
          setNadaValuationType(VALUATION_TYPES.Nada);
          setNadaValuitionTypePosition((prevState) => prevState + 1);
        } else {
          Notistack.generalError('JD Power Trim is required');
        }
      } else {
        // end workflow for invalid form
        setNadaValuationPulled(true);
      }
    },
    [values]
  );

  const handleKbbClick = useCallback(
    (showPopup: boolean) => {
      if (validate(VALUATION_TYPES.Kbb)) {
        if (values.kbbTrim) {
          setAutoSubmitOptionsPopup(!showPopup);
          setKbbValuationType(VALUATION_TYPES.Kbb);
          setKbbValuitionTypePosition((prevState) => prevState + 1);
        } else {
          Notistack.generalError(`${VALUATION_TYPES.Kbb} Trim is required`);
        }
      } else {
        // end workflow for invalid form
        setKbbValuationPulled(true);
      }
    },
    [values]
  );

  useEffect(() => {
    setNadaValuationPulled(false);
    setKbbValuationPulled(false);
    if (autoPullValuation) {
      setAutoSubmitOptionsPopup(true);
      handleKbbClick(false);
      handleNadaClick(false);
    }
  }, [autoPullValuation]);

  useEffect(() => {
    if (nadaValuationPulled && kbbValuationPulled && autoPullValuation) {
      autoPullValuationComplete(true);
    } else {
      autoPullValuationComplete(false);
    }
  }, [nadaValuationPulled, kbbValuationPulled, autoPullValuation]);

  const handleValuationPullResult = useCallback(
    (isSuccess: boolean, valuationType: VALUATION_TYPES) => {
      if (isSuccess) {
        if (valuationType === VALUATION_TYPES.Nada) {
          setNadaValuationPulled(true);
        } else if (valuationType === VALUATION_TYPES.Kbb) {
          setKbbValuationPulled(true);
        }
      }
    },
    [autoPullValuation]
  );

  const handleMSRPBtn = useCallback(async () => {
    const { id, vin } = values;

    setMsrpLoadingStatus(true);

    if (!id) {
      Notistack.generalError('Please save asset or select an existing asset');
      return;
    }

    if (!vin) {
      validateField('vin');
      Notistack.generalError('Vin are required!');

      return;
    }

    const msrpData = await requestMSRPData(id, vin);

    setFieldValue('msrp', msrpData?.retailPrice ?? null);

    if (msrpData?.stickerPdfUrl) {
      openPdfByUrl(msrpData?.stickerPdfUrl);
    }
    setMsrpLoadingStatus(false);
  }, [values]);

  const handleCloseNada = useCallback(() => {
    setNadaValuationType(null);
    setNadaValuitionTypePosition(0);
  }, []);

  const handleCloseKbb = useCallback(() => {
    setKbbValuationType(null);
    setKbbValuitionTypePosition(0);
  }, []);

  const handleDisplayVehicleOption = useCallback((showPopup: boolean) => {
    setDisplayVehicleOptionsPopup(showPopup);
  }, []);

  const handleCloseVehicleOptions = useCallback(() => {
    setDisplayVehicleOptionsPopup(false);
  }, []);
  const formConfig = [
    {
      width: '40%',
      size: 'medium',
      component: () => (
        <Button
          variant="contained"
          color="info"
          onClick={() => handleNadaClick(true)}
        >
          JD Power
        </Button>
      ),
    },
    {
      width: '100%',
      name: 'nada.baseRetailAmount',
      label: 'JD Power',
      decimalScale: null,
      prefix: '$',
      component: NumberInputField,
      disabled: !nadaValuationFieldsEdit,
    },
    {
      width: '100%',
      name: 'nada.baseTradeInAmount',
      label: 'Trade In Amount',
      decimalScale: null,
      prefix: '$',
      component: NumberInputField,
      disabled: !nadaValuationFieldsEdit,
    },
    {
      width: '40%',
      size: 'medium',
      component: () => (
        <Button
          variant="contained"
          color="info"
          onClick={() => handleKbbClick(true)}
        >
          KBB
        </Button>
      ),
    },
    {
      width: '100%',
      name: 'kbb.baseRetailAmount',
      label: 'KBB',
      decimalScale: null,
      prefix: '$',
      component: NumberInputField,
      disabled: !kbbValuationFieldsEdit,
    },
    {
      width: '100%',
      name: 'kbb.baseWholesaleAmount',
      label: 'KBB Wholesale',
      decimalScale: null,
      prefix: '$',
      component: NumberInputField,
      disabled: !kbbValuationFieldsEdit,
    },
    {
      width: '100%',
      name: 'kbb.baseFinanceExpressAmount',
      label: 'KBB Finance Express',
      decimalScale: null,
      prefix: '$',
      component: NumberInputField,
      disabled: !kbbValuationFieldsEdit,
    },
    {
      width: '100%',
      name: 'bumper.high',
      label: 'Bumper High',
      decimalScale: null,
      prefix: '$',
      component: NumberInputField
    },
    {
      width: '100%',
      name: 'bumper.avg',
      label: 'Bumper Avg',
      decimalScale: null,
      prefix: '$',
      component: NumberInputField
    },
    {
      width: '100%',
      name: 'bumper.low',
      label: 'Bumper Low',
      decimalScale: null,
      prefix: '$',
      component: NumberInputField
    },
    {
      width: '40%',
      size: 'medium',
      component: () => (
        <Button
          variant="contained"
          disabled={disableMSRP}
          color="info"
          onClick={handleMSRPBtn}
        >
          {isMsrpLoading ? <LoaderBackdrop open={isMsrpLoading} /> : 'MSRP'}
        </Button>
      ),
    },
    {
      width: '100%',
      name: 'msrp',
      label: 'MSRP',
      decimalScale: null,
      prefix: '$',
      component: NumberInputField,
    },
    {
      width: '100%',
      name: 'bookOutDate',
      label: 'Bookout Date',
      component: SelectDateField,
    },
  ];

  return (
    <ValuationWrapper>
      <VehicleOptionsButton
        variant="contained"
        size="small"
        disabled={values?.vin?.length === 0}
        onClick={() => handleDisplayVehicleOption(true)}
      >
        Vehicle options
      </VehicleOptionsButton>
      <FormPart
        name="Valuation"
        config={formConfig}
        marginTop={1}
        marginBottom={2}
      />
      {nadaValuationType && (
        <VehicleFeaturesModal
          pickedDealId={pickedDealId}
          vehicleFeatureModalPosition={nadaValueTypePosition}
          valuationType={nadaValuationType}
          zip={zip}
          autoSubmitOptionsPopup={autoSubmitOptionsPopup}
          handleValuationPull={(isSuccess: boolean) => handleValuationPullResult(isSuccess, VALUATION_TYPES.Nada)}
          handleClose={handleCloseNada}
        />
      )}

      {kbbValuationType && (
        <VehicleFeaturesModal
          pickedDealId={pickedDealId}
          vehicleFeatureModalPosition={kbbValueTypePosition}
          valuationType={kbbValuationType}
          zip={zip}
          autoSubmitOptionsPopup={autoSubmitOptionsPopup}
          handleValuationPull={(isSuccess: boolean) => handleValuationPullResult(isSuccess, VALUATION_TYPES.Kbb)}
          handleClose={handleCloseKbb}
        />
      )}
      {displayVehicleOptionsPopup && (
        <VehicleOptionsModal
          displayVehicleOptionsPopup={displayVehicleOptionsPopup}
          handleClose={handleCloseVehicleOptions}
        />
      )}
    </ValuationWrapper>
  );
};
